import { DateTime } from "luxon";

export function formatDate(dateString: string) {
  const date = DateTime.fromISO(dateString);
  return date.toFormat("yyyy-MM-dd HH:mm:ss");
}

export function formatString(template: string, ...args: unknown[]): string {
  return template.replace(/{(\d+)}/g, (substring, ...words) => {
    return (args[parseInt(words[0])] as string) ?? substring;
  });
}

export function getEchartsMax(value: number, splitNumber = 6): number {
  if (value <= 0) return 1;

  const minStep = value / splitNumber;
  const step = _getNiceStepAboveLegacy(minStep);
  return step * splitNumber;
}

function _getNiceStepAboveLegacy(minStep: number): number {
  const niceFactors = [1, 2, 5];
  const exponent = Math.floor(Math.log10(minStep));

  for (let exp = exponent; exp < exponent + 10; exp++) {
    const base = 10 ** exp;
    for (const factor of niceFactors) {
      const step = factor * base;
      if (step >= minStep) return step;
    }
  }

  // fallback, shouldn't hit
  return Math.ceil(minStep);
}

// ---------------------------------------------------------------------------
// NiceScale — ported from Flutter nice_scale.dart
// Uses nice factors [1, 2, 2.5, 5, 10] matching the ECharts algorithm.
// ---------------------------------------------------------------------------
export interface NiceScaleResult {
  /** Nice lower bound (>= 0 when data is non-negative) */
  min: number;
  /** Nice upper bound */
  max: number;
  /** Distance between each tick */
  step: number;
  /** Number of ticks (inclusive of both min and max) */
  tickCount: number;
  /** Tick values array, from min to max */
  ticks: number[];
}

const NICE_FACTORS = [1.0, 2.0, 2.5, 5.0, 10.0];

function _niceStep(roughStep: number): number {
  if (roughStep <= 0) return 1;
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const fraction = roughStep / magnitude;
  for (const f of NICE_FACTORS) {
    if (fraction <= f) return f * magnitude;
  }
  return 10 * magnitude;
}

function _buildResult(min: number, max: number, step: number, tickCount: number): NiceScaleResult {
  const ticks: number[] = [];
  for (let v = min; v <= max + step * 0.001; v += step) {
    // round to avoid floating-point noise (e.g. 0.30000000000000004)
    ticks.push(Math.round(v / step) * step);
  }
  return { min, max, step, tickCount, ticks };
}

/**
 * Compute a nice scale matching Flutter's `NiceScale.compute()`.
 *
 * @param rawMin  Actual minimum value in the data
 * @param rawMax  Actual maximum value in the data
 * @param targetTicks  Desired number of ticks (default 5, excludes 0 if min = 0)
 * @param exactTicks   If set, forces the tick count to exactly this value (used to sync two Y-axes)
 */
export function computeNiceScale(
  rawMin: number,
  rawMax: number,
  targetTicks = 5,
  exactTicks?: number
): NiceScaleResult {
  // Edge-case: empty or constant data
  if (rawMin === rawMax) {
    const ticks = exactTicks ?? targetTicks;
    if (rawMin === 0) {
      const step = 10.0 / ticks;
      return _buildResult(0, 10, step, ticks);
    }
    const fallback = Math.abs(rawMin) * 0.1;
    const lo = rawMin >= 0 ? 0.0 : rawMin - fallback;
    const hi = rawMax + fallback;
    const step = (hi - lo) / ticks;
    return _buildResult(lo, hi, step, ticks);
  }

  if (exactTicks !== undefined) {
    const niceMin = rawMin < 0 ? rawMin : 0.0;
    const range = rawMax - niceMin;
    const roughStep = range / exactTicks;
    const niceStep = _niceStep(roughStep);
    const finalMin = rawMin < 0 ? Math.floor(rawMin / niceStep) * niceStep : 0.0;
    const finalMax = finalMin + niceStep * exactTicks;
    return _buildResult(finalMin, finalMax, niceStep, exactTicks);
  }

  // Always start from 0 for non-negative data (matching Flutter _computeY)
  const niceMin = rawMin < 0 ? rawMin : 0.0;
  const range = rawMax - niceMin;
  const roughStep = range / targetTicks;
  const niceStep = _niceStep(roughStep);

  const floorMin = Math.floor(niceMin / niceStep) * niceStep;
  const ceilMax = Math.ceil(rawMax / niceStep) * niceStep;
  const count = Math.round((ceilMax - floorMin) / niceStep);

  return _buildResult(floorMin, ceilMax, niceStep, count);
}

export function getRelativeTime(time: DateTime): string {
  const now = DateTime.now();
  const diff = now
    .diff(time, ["years", "months", "days", "hours", "minutes", "seconds"])
    .toObject();

  if (diff.years && diff.years >= 1) {
    return `${Math.floor(diff.years + (diff.months ?? 0) / 12)} year${Math.floor(diff.years) > 1 ? "s" : ""} ago`;
  }
  if (diff.months && diff.months >= 1) {
    return `${Math.round(diff.months + (diff.days ?? 0) / 30)} month${Math.floor(diff.months) > 1 ? "s" : ""} ago`;
  }
  if (diff.days && diff.days >= 1) {
    return `${Math.floor(diff.days + (diff.hours ?? 0) / 24)} day${Math.floor(diff.days) > 1 ? "s" : ""} ago`;
  }
  if (diff.hours && diff.hours >= 1) {
    return `${Math.floor(diff.hours + (diff.minutes ?? 0) / 60)} hour${Math.floor(diff.hours) > 1 ? "s" : ""} ago`;
  }
  if (diff.minutes && diff.minutes >= 1) {
    return `${Math.floor(diff.minutes + (diff.seconds ?? 0) / 60)} minute${Math.floor(diff.minutes) > 1 ? "s" : ""} ago`;
  }
  if (diff.seconds && diff.seconds >= 1) {
    return `${Math.floor(diff.seconds)} second${Math.floor(diff.seconds) > 1 ? "s" : ""} ago`;
  }
  return "just now";
}
